import React from 'react'
import PropTypes from 'prop-types'
import { Box, Card, getPaletteColor } from 'pcln-design-system'
import styled from 'styled-components'
import { mediaQueries } from 'hotel-component-helpers'
import MediaQueryMatch from '@pcln/media-query-match'

const propTypes = {
  leftBarColor: PropTypes.string,
  messageComponent: PropTypes.node,
  animationDelay: PropTypes.number,
  animationStickinessDelay: PropTypes.number,
  bottom: PropTypes.bool,
  persist: PropTypes.bool,
}

const defaultProps = {
  leftBarColor: 'primary.base',
  animationDelay: 3,
  animationStickinessDelay: 10,
  persist: false,
}

const getCustomLargeBoxAnimation = (props) => `
  0% {
    visibility: visible;
    opacity: 1;
  }
  ${
    props.persist
      ? `
        100% {
          transform: translateX(-532px);
          opacity: 1;
          visibility: visible;
        }
      `
      : `
        5%,
        98% {
          transform: translateX(-532px);
          visibility: visible;
          opacity: 1;
        }
        100% {
          transform: translateX(532px);
          visibility: visible;
          opacity: 0;
        }
      `
  }
`

const getCustomSmallBoxAnimation = (props) => `
  0% {
    ${props.bottom ? 'bottom: -40px;' : 'top: -40px;'}
    visibility: visible;
    opacity: 1;
  }
  ${
    props.persist
      ? `
        100% {
          right: 16px;
          ${props.bottom ? 'bottom: 8px;' : 'top: 8px;'}
          visibility: visible;
          opacity: 1;
        }
      `
      : `
        .5%,
        99% {
          right: 16px;
          ${props.bottom ? 'bottom: 8px;' : 'top: 8px;'}
          visibility: visible;
          opacity: 1;
        }
        100% {
          ${props.bottom ? 'bottom: -40px;' : 'top: -40px;'}
          visibility: visible;
          opacity: 0;
        }
      `
  }
  
`

const MessageSystemBox = styled(Box)`
  position: fixed;
  animation: slide ${(props) => props.animationStickinessDelay}s linear 1 normal
    ${(props) => (props.persist ? 'forwards' : '')};
  animation-delay: ${(props) => props.animationDelay}s;
  z-index: 10000;
  opacity: 0;
  visibility: hidden;
`

const MessageSystemBoxSmall = styled(MessageSystemBox)`
  ${(props) => (props.bottom ? 'bottom: 0;' : 'top: 0;')}
  right: 16px;
`

const MessageSystemBoxLarge = styled(MessageSystemBox)`
  ${(props) => (props.bottom ? 'bottom: 8px;' : 'top: 8px;')}
  right: -500px;
  margin-top: 32px;
`

const AnimationBoxSmall = styled(MessageSystemBoxSmall)`
  @keyframes slide {
    ${getCustomSmallBoxAnimation}
  }
`

const AnimationBoxLarge = styled(MessageSystemBoxLarge)`
  @keyframes slide {
    ${getCustomLargeBoxAnimation}
  }
`

const StyledCard = styled(Card)`
  border-left: 8px solid ${(props) => getPaletteColor(props.leftBarColor, 'base')};
  width: 420px;
  ${mediaQueries.phoneOnly`
    width: 100%;
  `}
`
class MessageSystemAnimation extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    animationDelay: PropTypes.number,
    animationStickinessDelay: PropTypes.number,
    bottom: PropTypes.bool,
    persist: PropTypes.bool,
  }

  state = {
    hasTriggeredAnimation: false,
  }

  componentDidMount() {
    this.setState({
      hasTriggeredAnimation: true,
    })
  }

  render() {
    const { children, bottom, animationDelay, animationStickinessDelay, persist } = this.props
    const { hasTriggeredAnimation } = this.state

    const SmallBox = hasTriggeredAnimation ? MessageSystemBoxSmall : AnimationBoxSmall
    const LargeBox = hasTriggeredAnimation ? MessageSystemBoxLarge : AnimationBoxLarge

    return (
      <React.Fragment>
        <MediaQueryMatch maxWidth='md'>
          <SmallBox
            bottom={bottom}
            animationDelay={animationDelay}
            animationStickinessDelay={animationStickinessDelay}
            persist={persist}
            data-testid='MESSAGE_SYSTEM_SMALL'
          >
            {children}
          </SmallBox>
        </MediaQueryMatch>
        <MediaQueryMatch minWidth='md'>
          <LargeBox
            bottom={bottom}
            animationDelay={animationDelay}
            animationStickinessDelay={animationStickinessDelay}
            persist={persist}
            data-testid='MESSAGE_SYSTEM_LARGE'
          >
            {children}
          </LargeBox>
        </MediaQueryMatch>
      </React.Fragment>
    )
  }
}

const MessageSystem = ({
  leftBarColor,
  messageComponent,
  animationDelay,
  animationStickinessDelay,
  bottom,
  persist,
}) => {
  return (
    <MessageSystemAnimation
      bottom={bottom}
      animationDelay={animationDelay}
      animationStickinessDelay={animationStickinessDelay}
      persist={persist}
    >
      <StyledCard
        leftBarColor={leftBarColor}
        boxShadowSize='xl'
        borderWidth={0}
        borderRadius={3}
        p={2}
        mx={2}
        mb={2}
        bg='background.lightest'
      >
        {messageComponent}
      </StyledCard>
    </MessageSystemAnimation>
  )
}

MessageSystem.propTypes = propTypes
MessageSystem.defaultProps = defaultProps
export default MessageSystem
